import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Card,
  Stack,
  Typography,
  TextField,
  Button,
  IconButton,
  CircularProgress,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SaveIcon from "@mui/icons-material/Save";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate, useParams } from "react-router-dom";
import {
  useGetPermissionMatrixModulesQuery,
  useGetRolePermissionQuery,
  useGetUserRoleQuery,
  useUpdateRolePermissionMutation,
  useCreateUserRoleMutation,
} from "../../../Api/attoDeskApi";
import { IUserRole } from "../../../Api/Interface/api.interface";
import { useNotifier } from "../../../Core/Notifier";
import { toProperCase } from "../../../Components/Validation/toProperCase";
import PermissionMatrix from "../Components/PermissionMatrix";
import useBeforeUnload from "../Components/useBeforeUnload";
import { MatrixGroup, MatrixFlag } from "../Components/permissionUtils";
import { useConfirm } from "../../../Components/Confirm";

const EditRole: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const roleId = id ? Number(id) : undefined;
  const { showMessage, showErrorMessage } = useNotifier();

  const { data: rolesResp, isLoading: rolesLoading } = useGetUserRoleQuery({
    searchText: "",
  });
  const { data: modulesResp, isLoading: modulesLoading } =
    useGetPermissionMatrixModulesQuery();
  const { data: rolePermResp, isFetching: permsLoading } =
    useGetRolePermissionQuery({ roleId } as any, { skip: !roleId });

  const [updateRolePermission, { isLoading: savingPerms }] =
    useUpdateRolePermissionMutation();
  const [createUserRole, { isLoading: savingRole }] = useCreateUserRoleMutation();
  const saving = savingPerms || savingRole;

  const roles: IUserRole[] = Array.isArray(rolesResp?.data)
    ? (rolesResp?.data as IUserRole[])
    : [];
  const role = roles.find((r) => Number(r.id) === roleId);

  const groups: MatrixGroup[] = (modulesResp as any)?.data?.groups ?? [];
  const flags: MatrixFlag[] = (modulesResp as any)?.data?.flags ?? [];

  const [initialAssigned, setInitialAssigned] = useState<Set<number> | null>(null);
  const [initialName, setInitialName] = useState<string>("");
  const [assigned, setAssigned] = useState<Set<number>>(new Set());
  const [roleName, setRoleName] = useState("");

  // Seed once the role's permissions arrive.
  useEffect(() => {
    const rows: any[] = (rolePermResp as any)?.data ?? [];
    const seed = new Set<number>(
      rows.filter((p) => p.isAssigned).map((p) => p.id)
    );
    setAssigned(seed);
    setInitialAssigned(seed);
  }, [rolePermResp]);

  useEffect(() => {
    if (role && !initialName) {
      setRoleName(role.roleName);
      setInitialName(role.roleName);
    }
  }, [role, initialName]);

  const changedPermissionsCount = useMemo(() => {
    if (!initialAssigned) return 0;
    let n = 0;
    assigned.forEach((id) => {
      if (!initialAssigned.has(id)) n += 1;
    });
    initialAssigned.forEach((id) => {
      if (!assigned.has(id)) n += 1;
    });
    return n;
  }, [assigned, initialAssigned]);

  const nameChanged = toProperCase(roleName) !== toProperCase(initialName);
  const dirty = changedPermissionsCount > 0 || nameChanged;

  useBeforeUnload(dirty && !saving);

  const confirm = useConfirm();

  // React Router in-app nav guard via manual confirm on our own navigations.
  // Async (Promise-returning) after the useConfirm swap. Verified no caller
  // reads location/state immediately after guardedNavigate — both use sites
  // are inline onClick handlers that don't await the returned Promise; the
  // one-microtask sequencing delta has no observable effect here.
  const guardedNavigate = useCallback(
    async (to: string) => {
      if (dirty) {
        const ok = await confirm({
          title: "Discard unsaved changes?",
          message: "You have unsaved changes. Discard them and leave?",
          confirmText: "Discard and leave",
          cancelText: "Stay",
          variant: "warning",
        });
        if (!ok) return;
      }
      navigate(to);
    },
    [dirty, navigate, confirm]
  );

  const handleReset = async () => {
    if (!initialAssigned) return;
    if (!dirty) return;
    const ok = await confirm({
      title: "Revert to saved?",
      message: "Discard your changes and revert to the saved state?",
      confirmText: "Revert",
      cancelText: "Keep editing",
      variant: "warning",
    });
    if (!ok) return;
    setAssigned(new Set(initialAssigned));
    setRoleName(initialName);
  };

  const handleSave = async () => {
    if (!roleName.trim()) {
      showErrorMessage("Role name is required.");
      return;
    }
    if (!roleId) {
      showErrorMessage("Role ID missing.");
      return;
    }
    if (assigned.size === 0) {
      // Wording normalised to match NewRole ("Users assigned to it" — the
      // "with it" phrasing was ambiguous about what "it" referred to).
      const ok = await confirm({
        title: "Save role with no permissions?",
        message: "This role has no permissions granted. Users assigned to it will have no access. Save anyway?",
        confirmText: "Save anyway",
        cancelText: "Go back",
        variant: "warning",
      });
      if (!ok) return;
    }
    try {
      // Rename if needed.
      if (nameChanged) {
        const res: any = await createUserRole({
          id: role?.id,
          roleName: toProperCase(roleName),
          privileges: [],
        }).unwrap();
        if (res?.status === false) {
          showErrorMessage(res.message || "Failed to rename role.");
          return;
        }
      }
      const permRes: any = await updateRolePermission({
        roleId,
        permissionIds: Array.from(assigned),
      }).unwrap();
      if (permRes?.status === false) {
        showErrorMessage(permRes.message || "Failed to save permissions.");
        return;
      }
      setInitialAssigned(new Set(assigned));
      setInitialName(roleName);
      showMessage("Permissions updated.");
      navigate(`/user/role/${roleId}`);
    } catch (e: any) {
      showErrorMessage(e?.data?.message || "Something went wrong.");
    }
  };

  const loading = rolesLoading || modulesLoading || permsLoading;

  if (!loading && !role) {
    return (
      <Box sx={{ p: 4 }}>
        <Card sx={{ p: 4, textAlign: "center" }}>
          <Typography variant="h6" sx={{ mb: 1 }}>
            Role not found
          </Typography>
          <Button variant="outlined" onClick={() => navigate("/user/role/list")}>
            Back to roles
          </Button>
        </Card>
      </Box>
    );
  }

  return (
    <Box sx={{ background: "#f8fafc", minHeight: "100%" }}>
      {/* Header */}
      <Card
        sx={{
          borderRadius: 0,
          boxShadow: "none",
          borderBottom: "1px solid #e2e8f0",
          background: "#fff",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          sx={{ px: 3, py: 2 }}
        >
          <IconButton
            onClick={() => guardedNavigate(`/user/role/${roleId ?? ""}`)}
            size="small"
          >
            <ArrowBackIcon />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, color: "#0f172a" }}>
              Edit Permissions {role ? `— ${toProperCase(role.roleName)}` : ""}
            </Typography>
            <Typography variant="body2" sx={{ color: "#64748b" }}>
              Toggle permissions grouped by module.
            </Typography>
          </Box>
          {dirty && (
            <Chip
              label={`${
                changedPermissionsCount + (nameChanged ? 1 : 0)
              } unsaved change${
                changedPermissionsCount + (nameChanged ? 1 : 0) === 1 ? "" : "s"
              }`}
              sx={{ background: "#fef3c7", color: "#92400e", fontWeight: 600 }}
            />
          )}
        </Stack>
      </Card>

      <Box sx={{ p: 3, pb: 12 }}>
        {/* Role name */}
        <Box
          sx={{
            background: "#fff",
            border: "1px solid #e2e8f0",
            borderRadius: 2,
            p: 2,
            mb: 2,
          }}
        >
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 0.5 }}>
            Role Name <span style={{ color: "#dc2626" }}>*</span>
          </Typography>
          <TextField
            size="small"
            fullWidth
            value={roleName}
            onChange={(e) => setRoleName(e.target.value)}
            sx={{ maxWidth: 420 }}
          />
        </Box>

        <PermissionMatrix
          groups={groups}
          flags={flags}
          assigned={assigned}
          onChange={setAssigned}
          loading={loading}
          mode="edit"
        />
      </Box>

      {/* Sticky footer */}
      <Box
        sx={{
          position: "sticky",
          bottom: 0,
          left: 0,
          right: 0,
          background: "#fff",
          borderTop: "1px solid #e2e8f0",
          px: 3,
          py: 1.5,
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          boxShadow: "0 -2px 10px rgba(15,23,42,0.05)",
        }}
      >
        <Typography sx={{ fontSize: 13, color: "#64748b", flex: 1 }}>
          {dirty
            ? `${
                changedPermissionsCount + (nameChanged ? 1 : 0)
              } unsaved change${
                changedPermissionsCount + (nameChanged ? 1 : 0) === 1 ? "" : "s"
              }.`
            : "No changes."}
        </Typography>
        <Button
          onClick={handleReset}
          variant="text"
          color="inherit"
          disabled={!dirty || saving}
          startIcon={<HistoryIcon />}
        >
          Reset
        </Button>
        <Button
          onClick={() => guardedNavigate(`/user/role/${roleId ?? ""}`)}
          variant="outlined"
          color="inherit"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={saving || loading || !dirty || !roleName.trim()}
          startIcon={
            saving ? (
              <CircularProgress size={16} sx={{ color: "#fff" }} />
            ) : (
              <SaveIcon />
            )
          }
        >
          Save Changes
        </Button>
      </Box>
    </Box>
  );
};

export default EditRole;
